/**
 * Profile Photo Uploader Component
 * Reusable Alpine.js component for multi-photo upload with immediate upload on selection
 * Used by both getting-started flow and profile page photo editing
 */

import { createEmptySlot } from './photo-uploader.js';
import { photoSlotUploads } from './photo-slot-uploads.js';

const MAX_PHOTOS = 10;

/**
 * Profile photo uploader component factory
 * @param {Array} existingPhotos - Array of existing photos with {id, url} properties
 * @param {string} albumId - The album ID for the profile photos
 * @param {Object} options - Configuration options
 * @param {boolean} options.showSubmitButton - Show submit button (default: true)
 * @param {string} options.submitUrl - URL to navigate to on submit (default: /app/getting-started/done)
 * @param {boolean} options.requireMinimum - Require at least 1 photo (default: true)
 * @param {string} options.editAlbumUrl - URL to album edit page (shown when showSubmitButton is false)
 */
export function profilePhotoUploader(existingPhotos = [], albumId = null, options = {}) {
    const config = {
        showSubmitButton: true,
        submitUrl: '/app/getting-started/done',
        requireMinimum: true,
        editAlbumUrl: null,
        photoPageUrl: null,
        userId: null,
        ...options
    };

    const initialPhotos = Array.from({ length: MAX_PHOTOS }, (_, index) => {
        const existing = existingPhotos[index];
        if (existing) {
            return {
                file: null,
                preview: existing.url,
                thumbPreview: existing.thumbUrl || existing.url,
                uploading: false,
                uploaded: true,
                progress: 100,
                error: null,
                photoId: existing.id
            };
        }
        return { ...createEmptySlot(), thumbPreview: null };
    });

    return {
        ...photoSlotUploads,
        photos: initialPhotos,
        albumId: albumId,
        config: config,
        error: null,
        globalDragOver: false,
        dragOverSlot: null,
        activeSlotIndex: null,
        selectedPhotoIndex: null,
        deleteConfirmSlot: null,
        replaceConfirmSlot: null,
        pendingReplaceFile: null,
        uploadControllers: new Map(),
        nextUploadId: 0,

        get uploadedCount() {
            return this.photos.filter(p => p.uploaded).length;
        },

        get isAnyUploading() {
            return this.photos.some(p => p.uploading);
        },

        get canSubmit() {
            if (this.config.requireMinimum) {
                return this.photos[0].uploaded && !this.isAnyUploading;
            }
            return !this.isAnyUploading;
        },

        handleSlotClick(slotIndex) {
            if (this.photos[slotIndex].uploading) return;
            this.activeSlotIndex = slotIndex;
            this.$refs.fileInput.click();
        },

        viewPhoto(slotIndex) {
            const photo = this.photos[slotIndex];
            if (!photo.photoId || !this.config.photoPageUrl) return;
            const url = `${this.config.photoPageUrl}?pid=${photo.photoId}&uid=${this.config.userId}` +
                (this.albumId ? `&aid=${this.albumId}` : '');
            window.location.href = url;
        },

        handlePhotoTap(slotIndex) {
            if (this.photos[slotIndex].uploading) return;

            if (!this.photos[slotIndex].uploaded) {
                this.handleSlotClick(slotIndex);
                return;
            }

            if (this.selectedPhotoIndex === slotIndex) {
                this.selectedPhotoIndex = null;
            } else {
                this.selectedPhotoIndex = slotIndex;
            }
        },

        clearSelection() {
            this.selectedPhotoIndex = null;
        },

        handleFileSelect(event) {
            const files = Array.from(event.target.files || []);
            if (!files.length) return;

            this.error = null;
            const file = files[0];

            if (this.activeSlotIndex !== null) {
                const slot = this.photos[this.activeSlotIndex];
                if (slot.uploaded && slot.photoId) {
                    this.pendingReplaceFile = file;
                    this.replaceConfirmSlot = this.activeSlotIndex;
                    this.activeSlotIndex = null;
                    event.target.value = '';
                    return;
                }
                this.addFileToSlot(file, this.activeSlotIndex);
            } else {
                this.addFilesToEmptySlots(files);
            }

            this.activeSlotIndex = null;
            event.target.value = '';
        },

        cancelReplace() {
            this.replaceConfirmSlot = null;
            this.pendingReplaceFile = null;
        },

        async executeReplace() {
            const slotIndex = this.replaceConfirmSlot;
            const file = this.pendingReplaceFile;

            if (slotIndex === null || !file) return;

            const oldPhotoId = this.photos[slotIndex].photoId;
            this.replaceConfirmSlot = null;
            this.pendingReplaceFile = null;

            this.photos[slotIndex].oldPhotoIdToDelete = oldPhotoId;
            this.addFileToSlot(file, slotIndex);
        },

        addFileToSlot(file, slotIndex) {
            if (!file || !file.type.startsWith('image/')) return;

            const oldPhotoIdToDelete = this.photos[slotIndex].oldPhotoIdToDelete || null;

            const currentPreview = this.photos[slotIndex].preview;
            if (currentPreview && currentPreview.startsWith('blob:')) {
                URL.revokeObjectURL(currentPreview);
            }

            const blobUrl = URL.createObjectURL(file);
            this.startSlotUpload(file, slotIndex, {
                file,
                preview: blobUrl,
                thumbPreview: blobUrl,
                uploading: true,
                uploaded: false,
                progress: 0,
                error: null,
                photoId: null,
                oldPhotoIdToDelete
            });
        },

        addFilesToEmptySlots(files) {
            let fileIndex = 0;
            for (let i = 0; i < this.photos.length && fileIndex < files.length; i++) {
                if (!this.photos[i].preview && !this.photos[i].uploading) {
                    const file = files[fileIndex];
                    if (file && file.type.startsWith('image/')) {
                        this.addFileToSlot(file, i);
                        fileIndex++;
                    }
                }
            }
        },

        confirmDeletePhoto(slotIndex) {
            if (this.photos[slotIndex].uploading) return;

            if (this.config.requireMinimum && this.uploadedCount === 1 && this.photos[slotIndex].uploaded) {
                this.showError('You must have at least one profile photo.');
                return;
            }

            this.selectedPhotoIndex = null;

            if (this.photos[slotIndex].photoId) {
                this.deleteConfirmSlot = slotIndex;
                this.refreshIcons();
            } else {
                this.removePhotoLocally(slotIndex);
            }
        },

        cancelDelete() {
            this.deleteConfirmSlot = null;
        },

        refreshIcons() {
            this.$nextTick(() => {
                if (window.lucide) {
                    window.lucide.createIcons();
                }
            });
        },

        handleGlobalDrop(event) {
            this.globalDragOver = false;
            this.dragOverSlot = null;

            const files = Array.from(event.dataTransfer?.files || []);
            if (files.length) {
                this.addFilesToEmptySlots(files);
            }
        },

        showError(message) {
            this.error = message;
        },

        hideError() {
            this.error = null;
        },

        handleSubmit() {
            if (!this.canSubmit) {
                if (this.isAnyUploading) {
                    this.showError('Please wait for uploads to complete.');
                } else if (this.config.requireMinimum) {
                    this.showError('Please upload at least one photo.');
                }
                return;
            }

            if (this.config.submitUrl) {
                window.location.href = this.config.submitUrl;
            }
        }
    };
}
